import React, { useState, useEffect, useRef } from 'react';
import { Bot, X, Send, ArrowUp, Zap } from 'lucide-react';

const Chatbot = (props) => {
  const {
    budgetData,
    itemsNeedingReview,
    // --- UPDATED PROPS ---
    totalPlannedBudget,
    totalAllocatedBudget,
    totalPlanningVariance,
    totalAllocatedVariance, // RENAMED from totalExecutionVariance
    // --- END UPDATED PROPS ---
    formatCurrency,
    setFilterStatus,
    setSearchTerm,
    setSelectedRow,
    isChatOpen,
    setIsChatOpen
  } = props;

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const chatEndRef = useRef(null);

  // --- UPDATED ---
  const initialMessage = {
    id: 'init',
    sender: 'bot',
    text: `Hi! I'm Fin, your AI budget assistant. I've analyzed the latest data, and there are ${itemsNeedingReview} items that need your attention.`,
    suggestions: [
      'Show me items that need review',
      "What are the overall variances?",
      "Tell me about 'IT Operations'",
    ],
  };

  useEffect(() => {
    if (isChatOpen && messages.length === 0) {
      setMessages([initialMessage]);
    }
  }, [isChatOpen, messages.length, itemsNeedingReview]);
  // --- END UPDATED ---

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // --- getBotResponse - UPDATED ---
  const getBotResponse = (userInput) => {
    const input = userInput.toLowerCase().trim();

    // Rule-based responses
    if (input.includes('review') || input.includes('error')) {
      setFilterStatus('error');
      const errorItems = budgetData.filter(r => r.status === 'error').map(r => r.domain);
      return {
        text: `I've filtered the table to show items needing review. It looks like ${errorItems.join(', ')} have issues.`,
        suggestions: ["What's the status of 'Engineering'?", 'Show all items'],
      };
    }
    
    if (input.includes('all items') || input.includes('show all')) {
      setFilterStatus('all');
      setSearchTerm('');
      return {
        text: 'I\'ve reset the filter to show all items.',
        suggestions: ["How many items need review?", "What are the overall variances?"],
      };
    }

    if (input.includes('variance')) {
      const planPercent = totalPlannedBudget !== 0 ? ((totalPlanningVariance / totalPlannedBudget) * 100).toFixed(1) : '0.0';
      const allocPercent = totalAllocatedBudget !== 0 ? ((totalAllocatedVariance / totalAllocatedBudget) * 100).toFixed(1) : '0.0';

      return {
        text: `We have two key variances:
1.  **Planning Variance** (Allocated vs. Planned) is ${formatCurrency(totalPlanningVariance)} (${planPercent}%).
2.  **Allocated Variance** (Actuals vs. Allocated) is ${formatCurrency(totalAllocatedVariance)} (${allocPercent}%).`,
        suggestions: ['Which domain is most over budget?', 'Show me items on track'],
      };
    }

    if (input.includes('on track') || input.includes('success')) {
      setFilterStatus('success');
      return {
        text: 'I\'ve filtered the table to show items that are "On Track".',
        suggestions: ['Show me items that need review', 'Show all items'],
      };
    }
    
    // Check for domain names
    const domainMatch = budgetData.find(row => input.includes(row.domain.toLowerCase()));
    if (domainMatch) {
      setSelectedRow(domainMatch);
      return {
        text: `Opening the detailed analysis for '${domainMatch.domain}'...`,
        suggestions: ['Show me all items', "What are the overall variances?"],
      };
    }
    
    if (input.includes('hello') || input.includes('hi')) {
      return {
        text: "Hello! How can I help you with the budget today?",
        suggestions: initialMessage.suggestions,
      };
    }

    // Default response
    return {
      text: "I'm sorry, I can only assist with budget-related questions. You can ask me to 'show items that need review' or 'tell me about Engineering'.",
      suggestions: initialMessage.suggestions,
    };
  };
  // --- END getBotResponse ---

  const handleSend = () => {
    if (inputValue.trim() === '') return;

    const userMsg = { id: Date.now(), sender: 'user', text: inputValue };
    const newMessages = [...messages, userMsg];

    setMessages(newMessages);
    setInputValue('');

    setTimeout(() => {
      const botResponse = getBotResponse(inputValue);
      const botMsg = {
        id: Date.now() + 1,
        sender: 'bot',
        text: botResponse.text,
        suggestions: botResponse.suggestions,
      };
      setMessages([...newMessages, botMsg]);
    }, 500);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    // Directly call handleSend logic
    const userMsg = { id: Date.now(), sender: 'user', text: suggestion };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInputValue('');
    
    setTimeout(() => {
      const botResponse = getBotResponse(suggestion);
      const botMsg = {
        id: Date.now() + 1,
        sender: 'bot',
        text: botResponse.text,
        suggestions: botResponse.suggestions,
      };
      setMessages([...newMessages, botMsg]);
    }, 500);
  };

  const styles = {
    chatButton: {
      position: 'fixed',
      bottom: '2.5rem',
      right: '2.5rem',
      backgroundColor: '#2563EB', // blue-600
      color: 'white',
      padding: '1rem',
      borderRadius: '50%',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      cursor: 'pointer',
      zIndex: 999,
      border: 'none',
    },
    chatWindow: {
      position: 'fixed',
      bottom: 'calc(2.5rem + 5rem)', // 2.5rem + button height + margin
      right: '2.5rem',
      width: '400px',
      height: '500px',
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      zIndex: 999,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    },
    chatHeader: {
      backgroundColor: '#0F172A', // slate-900
      color: 'white',
      padding: '1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontWeight: '600',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: 'white',
      cursor: 'pointer',
    },
    chatBody: {
      flex: 1,
      overflowY: 'auto',
      padding: '1rem',
      backgroundColor: '#F1F5F9', // slate-100
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    message: {
      maxWidth: '80%',
      padding: '0.75rem',
      borderRadius: '0.5rem',
      lineHeight: '1.5',
      whiteSpace: 'pre-wrap', // To respect newlines in bot response
    },
    botMessage: {
      backgroundColor: 'white',
      color: '#0F172A', // slate-900
      alignSelf: 'flex-start',
      border: '1px solid #E2E8F0', // slate-200
    },
    userMessage: {
      backgroundColor: '#2563EB', // blue-600
      color: 'white',
      alignSelf: 'flex-end',
    },
    suggestions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    suggestionButton: {
      backgroundColor: '#DBEAFE', // blue-100
      color: '#1D4ED8', // blue-700
      border: '1px solid #BFDBFE', // blue-200
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.875rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    chatInputArea: {
      borderTop: '1px solid #CBD5E1', // slate-300
      padding: '1rem',
      backgroundColor: 'white',
      display: 'flex',
      gap: '0.5rem',
    },
    textInput: {
      flex: 1,
      border: '1px solid #CBD5E1', // slate-300
      borderRadius: '0.375rem',
      padding: '0.5rem 0.75rem',
      fontSize: '1rem',
    },
    sendButton: {
      backgroundColor: '#2563EB', // blue-600
      color: 'white',
      border: 'none',
      borderRadius: '0.375rem',
      padding: '0.5rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }
  };

  return (
    <>
      {!isChatOpen && (
        <button style={styles.chatButton} onClick={() => setIsChatOpen(true)}>
          <Bot size={24} />
        </button>
      )}

      {isChatOpen && (
        <div style={styles.chatWindow}>
          <div style={styles.chatHeader}>
            <div style={styles.headerTitle}>
              <Bot size={20} />
              <span>Fin, your AI Assistant</span>
            </div>
            <button style={styles.closeButton} onClick={() => setIsChatOpen(false)}>
              <X size={20} />
            </button>
          </div>

          <div style={styles.chatBody}>
            {messages.map((msg) => (
              <div key={msg.id} style={{ ...styles.message, ...(msg.sender === 'bot' ? styles.botMessage : styles.userMessage) }}>
                {msg.text}
                {msg.suggestions && (
                  <div style={styles.suggestions}>
                    {msg.suggestions.map((s, index) => (
                      <button key={index} style={styles.suggestionButton} onClick={() => handleSuggestionClick(s)}>
                        <Zap size={14} />
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          <div style={styles.chatInputArea}>
            <input
              type="text"
              style={styles.textInput}
              placeholder="Ask about the budget..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            />
            <button style={styles.sendButton} onClick={handleSend}>
              <Send size={20} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;